/**
 * Zod validation schemas for form data.
 */

import { z } from 'zod';

/**
 * Nullable non-negative integer for uncertainty fields.
 *
 * Accepts the values these number inputs actually hold at runtime — a string
 * (while typing), a number (from `setValue`/defaults), or null — and coerces to
 * `number | null` on output. Empty string and null both mean "not decided".
 *
 * The union arms are spelled out (rather than `z.coerce.number()`) so the INPUT
 * type stays `string | number | null` instead of `unknown`; resolvers v5 + zod 4
 * propagate the input type to the form's field values, and `unknown` there would
 * poison every consumer of these fields.
 *
 * null = not decided, 0 = deliberately no uncertainty.
 */
const nullableNonNegativeInt = z
  .union([z.string(), z.number(), z.null()])
  .transform((val) => (val === '' || val === null ? null : Number(val)))
  .refine((val) => val === null || (Number.isInteger(val) && val >= 0), {
    message: 'Must be a non-negative whole number',
  });

/**
 * Required non-negative integer that shows a proper error for empty values.
 * Used for work items which must be explicitly set.
 *
 * Input type is `string | number` (the runtime field value); output is `number`.
 */
const requiredNonNegativeInt = z
  .union([z.string(), z.number()])
  .refine((val) => val !== '' && val !== undefined && val !== null, { message: 'Work items is required' })
  .transform((val) => {
    const num = Number(val);
    if (isNaN(num)) throw new Error('Work items must be a number');
    return num;
  })
  .refine((val) => Number.isInteger(val), { message: 'Must be a whole number' })
  .refine((val) => val >= 0, { message: 'Must be non-negative' });

/**
 * Most runs a simulation may ask for — the API's cap (`MAX_TRIALS`,
 * shipcast-gz8). The run-count input and the form schema both use it.
 */
export const MAX_SIMULATION_RUNS = 10_000;

/**
 * Positive-integer count input (`num_simulations`).
 *
 * Like the helpers above, spelled out as a `string | number` union so the input
 * type is clean. Empty string coerces to 0 and fails the `.min(1)` check, exactly
 * as `z.coerce.number()` did before.
 */
const simulationCount = z
  .union([z.string(), z.number()])
  .transform((val) => Number(val))
  .pipe(
    z
      .number()
      .int()
      .min(1, 'At least 1 run required')
      .max(MAX_SIMULATION_RUNS, `Maximum ${MAX_SIMULATION_RUNS.toLocaleString('en-US')} runs`),
  );

/**
 * Schema for feature input.
 * id is optional (only set for features saved to database)
 */
const featureSchema = z.object({
  id: z.string().optional(), // Database UUID (set after save)
  name: z.string().min(1, 'Feature name is required'),
  source_id: z.string().nullable().optional(), // External source ID (e.g. "PROJ-123")
  source_url: z.string().nullable().optional(), // External tracker URL
  due_date: z.string().nullable().optional(), // Target delivery date (YYYY-MM-DD)
  status: z.string().nullable().optional(), // Tracker-native status (read-only; written by feature import)
});

/**
 * A deliverable's min/max pair rules — the schema refines below and
 * DeliverableForm's auto-save hold share them (shipcast-97d1): a pair that
 * breaks one is neither shown valid nor sent, since the API judges the stored
 * row merged with the patch and refuses it.
 */
export function unknownRangeIsOrdered(min: number | null, max: number | null): boolean {
  return min === null || max === null || max >= min;
}

/** An available-to-start max needs a min (dates as YYYY-MM-DD, empty = unset). */
export function availableToStartHasMin(min: string | null | undefined, max: string | null | undefined): boolean {
  return !max || !!min;
}

/** An available-to-start max is not before its min (YYYY-MM-DD compares as text). */
export function availableToStartIsOrdered(min: string | null | undefined, max: string | null | undefined): boolean {
  return !min || !max || max >= min;
}

/**
 * Schema for deliverable input.
 * Uses feature_id (UUID) for feature reference instead of feature_name.
 * id is optional (only set for deliverables saved to database)
 */
const deliverableSchema = z
  .object({
    id: z.string().optional(), // Database UUID (set after save)
    feature_id: z.string().nullable().default(null), // UUID or null for ungrouped
    name: z.string().min(1, 'Deliverable name is required'),
    unfinished_work_items: requiredNonNegativeInt,
    unknown_min: nullableNonNegativeInt, // null = not decided, 0 = deliberately no uncertainty
    unknown_max: nullableNonNegativeInt, // null = not decided, 0 = deliberately no uncertainty
    predecessors: z.array(z.string()), // Deliverable UUIDs
    due_date: z.string().optional().default(''),
    available_to_start_min: z.string().optional().default(''),
    available_to_start_max: z.string().optional().default(''),
    source_id: z.string().nullable().optional(), // External source ID (e.g. "PROJ-123")
    source_url: z.string().nullable().optional(), // External tracker URL
  })
  .refine((data) => unknownRangeIsOrdered(data.unknown_min, data.unknown_max), {
    message: 'Max uncertainty must be >= min uncertainty',
    path: ['unknown_max'],
  })
  .refine((data) => availableToStartHasMin(data.available_to_start_min, data.available_to_start_max), {
    message: 'Available-from start date is required when end date is set',
    path: ['available_to_start_min'],
  })
  .refine((data) => availableToStartIsOrdered(data.available_to_start_min, data.available_to_start_max), {
    message: 'Available-to date must be >= available-from date',
    path: ['available_to_start_max'],
  });

/**
 * Schema for team input.
 * id is optional (only set for teams saved to database)
 */
const teamSchema = z.object({
  id: z.string().optional(), // Database UUID (set after save)
  name: z.string().min(1, 'Team name is required'),
  throughput_data: z
    .string()
    .min(1, 'Throughput data is required')
    .refine(
      (val) => {
        const nums = val.split(',').map((s) => parseFloat(s.trim()));
        return nums.every((n) => !isNaN(n) && n >= 0);
      },
      {
        message: 'Invalid throughput data. Use comma-separated positive numbers.',
      },
    )
    // The server refuses a series with no completed work: a forecast cannot
    // sample it (shipcast-d8r.47; same rule as isSampleable in throughputCut.ts).
    .refine((val) => val.split(',').some((s) => parseFloat(s.trim()) > 0), {
      message: 'Throughput data needs at least one day with completed work.',
    }),
  // Set exclusively via StepperInput's setValue (always a number), so a plain
  // z.number() keeps the input type clean — no coercion from a raw text input.
  throughput_scale: z.number().min(0.01, 'Scale must be at least 1%').max(10, 'Scale cannot exceed 1000%').default(1.0),
  // Empty is valid: a team may have all deliverables removed (e.g. to re-import).
  // The store is mutable; "at least one deliverable" was an Excel-import-era relic.
  deliverables: z.array(deliverableSchema),
  wip: z.number().int().min(1, 'WIP must be at least 1'),
  color: z.string().optional(), // Hex color code like '#636df1'
});

/**
 * Schema for the complete simulation form.
 */
export const simulationFormSchema = z.object({
  name: z.string(),
  description: z.string(), // Empty string = null when saving
  // Required (not `.default([])`): the form always supplies an array, and a
  // default would make the input type `features?: …[]`, forcing undefined-checks
  // on every consumer that reads `values.features`.
  features: z.array(featureSchema),
  teams: z.array(teamSchema).min(1, 'At least one team is required'),
  num_simulations: simulationCount,
  start_date: z
    .string()
    .min(1, 'Start date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format'),
  // Required (not `.optional().default('')`): the form always supplies a string
  // (empty = "random seed"), so keeping it required avoids `string | undefined`.
  seed: z.string(),
});

/**
 * Form value types — the single source of truth for the simulation form.
 *
 * `SimulationFormValues` is the INPUT type (what react-hook-form field values
 * hold pre-validation: number inputs may carry strings, defaulted fields may be
 * absent). `SimulationFormData` is the OUTPUT type (post-coercion: clean numbers,
 * defaults applied) — what `handleSubmit` delivers and what gets sent to the API.
 *
 * These are re-exported from `@/types/api` so consumers keep a stable import path.
 */
export type SimulationFormValues = z.input<typeof simulationFormSchema>;
export type SimulationFormData = z.output<typeof simulationFormSchema>;

export type FeatureFormValues = z.input<typeof featureSchema>;
export type TeamFormValues = z.input<typeof teamSchema>;
export type DeliverableFormValues = z.input<typeof deliverableSchema>;

/**
 * Parse throughput string to number array.
 */
export function parseThroughputData(data: string): number[] {
  return data
    .split(',')
    .map((s) => parseFloat(s.trim()))
    .filter((n) => !isNaN(n));
}
